/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module SharedModule
 */

import {
    Directive,
    ElementRef,
    Inject,
    Input,
    OnInit,
    Renderer2,
} from '@angular/core';

import {
    AbstractControl,
    NG_VALIDATORS,
    ValidationErrors,
    Validator,
} from '@angular/forms';

import { L10nService } from '@vmw/ngx-vip';
import {
    each,
    isUndefined,
} from 'underscore';

import { IFieldInputAttrsHash, SchemaService } from 'ajs/modules/core/services';

import { configFieldInputValidator }
    from 'ng/modules/avi-forms/validators/config-field-input-validation';

import * as l10n from './config-field-input-validation.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description
 *      This directive sets min, max, and step attributes to the input field.
 *      Invalidates the form when the value is out of the boundaries (min-max).
 * @author Aravindh Nagarajan
 */
@Directive({
    providers: [
        {
            multi: true,
            provide: NG_VALIDATORS,
            useExisting: ConfigFieldInputValidationDirective,
        },
    ],
    selector: '[configFieldInputValidation]',
})
export class ConfigFieldInputValidationDirective implements OnInit, Validator {
    /**
     * ObjectType of the field.
     */
    @Input() public objectType: string;

    /**
     * Field name from Schema.
     */
    @Input() public fieldName: string;

    /**
     * Min value of the field - Used to validate formControl value.
     */
    private minValue: number | undefined;

    /**
     * max value of the field - Used to validate formControl value.
     */
    private maxValue: number | undefined;

    /**
     * Step value of the field - Used to validate formControl value.
     */
    private stepValue: number | 'any' | undefined;

    /**
     * Special Values of the field - Used to validate formControl value.
     */
    private specialValues: number[] | undefined;

    public constructor(
        @Inject(SchemaService) private schemaService: SchemaService,
        private renderer: Renderer2,
        private elementRef: ElementRef,
        private readonly l10nService: L10nService,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    /**
     * @override
     */
    public ngOnInit(): void {
        const fieldAttributes = this.getFieldInputAttributes();

        const {
            min,
            max,
            step,
            specialValues,
        } = fieldAttributes;

        this.minValue = min;
        this.maxValue = max;
        this.specialValues = specialValues;
        this.stepValue = step;

        this.setAttributesInHostElement(fieldAttributes);
    }

    /**
     * @override
     * Validate user input.
     * First check if validity.badInput is true. This is true if, for example, the user inputs 'e' or '--'. Angular
     * converts the value to null, so control.value never gets set to these badInput values.
     *
     * Check whether the input value is within min-max boundary.
     *
     * Return null if its a valid value, returns validationError if input is invalid.
     */
    public validate(control: AbstractControl): ValidationErrors | null {
        const { validity } = this.elementRef.nativeElement as HTMLInputElement;
        const { badInput } = validity;

        if (badInput) {
            return {
                invalidNumber: {
                    errorMessage: this.l10nService.getMessage(l10nKeys.invalidValueMessage),
                },
            };
        }

        return !isUndefined(control.value) ?
            configFieldInputValidator(
                this.minValue,
                this.maxValue,
                this.stepValue,
                this.specialValues,
            )(control) :
            null;
    }

    /**
     * Returns input field attributes (min, max and step) from SchemaService.
     */
    private getFieldInputAttributes(): IFieldInputAttrsHash {
        const {
            fieldName,
            objectType,
        } = this;

        return this.schemaService.getFieldInputAttributes(objectType, fieldName);
    }

    /**
     * Sets input field attributes (min, max and step) in host element.
     */
    private setAttributesInHostElement(fieldAttributes: IFieldInputAttrsHash): void {
        const {
            elementRef,
            renderer,
        } = this;

        const attributes: Array<[string, number]> = Object.entries(fieldAttributes);

        each(attributes, ([attr, value]) => {
            renderer.setAttribute(elementRef.nativeElement, attr, `${value}`);
        });
    }
}
